/**
 * Venue Analytics: how many teams (and people) turned up to each played live
 * event, per venue over time.
 *
 * Data comes from GET /api/analytics/attendance (admin-only). Optional
 * `from`/`to` ISO date params narrow the range server-side. The page shows a
 * summary card per venue, then either a chart (AttendanceChart) or a sortable
 * per-night table (AttendanceTable) over the same filtered points.
 *
 * Controls:
 *  - Time range: presets (3 / 6 / 12 months / All) that set the `from` param.
 *  - Venues: a multi-select set of toggle chips.
 *  - Metric (chart only): Teams / People / Both / Per team.
 *  - Lines (chart only): each night / rolling average / trend.
 */
import { useEffect, useMemo, useState } from 'react';
import { Box, Card, Flex, Grid, Heading, SegmentedControl, Text } from '@radix-ui/themes';
import api from '../../utils/api';
import AttendanceChart, { Overlay } from './AttendanceChart';
import AttendanceTable from './AttendanceTable';
import {
  AttendancePoint,
  MetricKind,
  ROLLING_WINDOW,
  VenueRef,
  colorForVenue,
  summarizeVenues,
} from './attendanceMath';
import './AttendanceGraph.css';

interface AttendanceData {
  venues: VenueRef[];
  points: AttendancePoint[];
  totals: { eventCount: number; teamCount: number; peopleCount: number };
}

interface AttendanceResponse {
  status: string;
  data: AttendanceData;
}

type RangePreset = '3m' | '6m' | '12m' | 'all';
type Metric = 'teams' | 'people' | 'both' | 'avg';
type View = 'chart' | 'table';

const RANGE_OPTIONS: ReadonlyArray<{ value: RangePreset; label: string }> = [
  { value: '3m', label: '3 mo' },
  { value: '6m', label: '6 mo' },
  { value: '12m', label: '12 mo' },
  { value: 'all', label: 'All time' },
];

const METRIC_OPTIONS: ReadonlyArray<{ value: Metric; label: string }> = [
  { value: 'teams', label: 'Teams' },
  { value: 'people', label: 'People' },
  { value: 'both', label: 'Both' },
  { value: 'avg', label: 'Per team' },
];

const OVERLAY_OPTIONS: ReadonlyArray<{ value: Overlay; label: string }> = [
  { value: 'raw', label: 'Each night' },
  { value: 'rolling', label: `${ROLLING_WINDOW}-night avg` },
  { value: 'trend', label: 'Trend' },
];

// Months back for each preset; `all` means no `from` bound.
function fromIsoForRange(range: RangePreset): string | null {
  const monthsByRange: Record<Exclude<RangePreset, 'all'>, number> = {
    '3m': 3,
    '6m': 6,
    '12m': 12,
  };
  if (range === 'all') {
    return null;
  }
  const d = new Date();
  d.setMonth(d.getMonth() - monthsByRange[range]);
  return d.toISOString();
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Box>
      <Text as="div" size="1" color="gray">
        {label}
      </Text>
      <Text as="div" size="4" weight="bold">
        {value}
      </Text>
    </Box>
  );
}

export default function AttendanceGraph(): JSX.Element {
  const [data, setData] = useState<AttendanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>('chart');
  const [range, setRange] = useState<RangePreset>('all');
  const [metric, setMetric] = useState<Metric>('teams');
  const [overlays, setOverlays] = useState<Set<Overlay>>(() => new Set<Overlay>(['raw', 'rolling']));
  // null = "not yet initialised"; once data loads we default to all venues.
  const [selectedVenues, setSelectedVenues] = useState<Set<number> | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const from = fromIsoForRange(range);
    const params = from ? { from } : undefined;
    api
      .get<AttendanceResponse>('/api/analytics/attendance', { params })
      .then((res) => {
        if (!active) {
          return;
        }
        setData(res.data.data);
        setError(null);
      })
      .catch(() => {
        if (active) {
          setError('Could not load attendance data.');
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [range]);

  const venues = useMemo<VenueRef[]>(() => data?.venues ?? [], [data]);

  // When new venue data arrives, default the selection to "all venues" the
  // first time, then keep whatever was selected that still exists.
  useEffect(() => {
    if (!data) {
      return;
    }
    setSelectedVenues((prev) => {
      const allIds = new Set(data.venues.map((v) => v.id));
      if (prev === null) {
        return allIds;
      }
      const kept = new Set([...prev].filter((id) => allIds.has(id)));
      return kept.size === 0 ? allIds : kept;
    });
  }, [data]);

  const activeVenues = useMemo<Set<number>>(
    () => selectedVenues ?? new Set(venues.map((v) => v.id)),
    [selectedVenues, venues]
  );

  function toggleInSet<T>(set: Set<T>, item: T): Set<T> {
    const next = new Set(set);
    if (next.has(item)) {
      next.delete(item);
    } else {
      next.add(item);
    }
    return next;
  }

  // Points after the venue filter, oldest-first (the API already sorts).
  const filtered = useMemo<AttendancePoint[]>(
    () => (data?.points ?? []).filter((p) => activeVenues.has(p.venueId)),
    [data, activeVenues]
  );

  const summaries = useMemo(() => summarizeVenues(filtered, venues), [filtered, venues]);

  const kinds = useMemo<MetricKind[]>(() => (metric === 'both' ? ['teams', 'people'] : [metric]), [metric]);

  const totalTeams = filtered.reduce((s, p) => s + p.teamCount, 0);
  const totalPeople = filtered.reduce((s, p) => s + p.peopleCount, 0);

  return (
    <Box className="attendance-graph">
      <Flex justify="between" align="start" gap="3" wrap="wrap" mb="4">
        <Box>
          <Heading size="6">Venue Analytics</Heading>
          <Text as="div" size="2" color="gray">
            {data
              ? `${filtered.length} played nights · ${totalTeams.toLocaleString()} teams · ${totalPeople.toLocaleString()} people`
              : 'Teams and people per played night, by venue and over time.'}
          </Text>
        </Box>
        <SegmentedControl.Root value={view} onValueChange={(v) => setView(v as View)}>
          <SegmentedControl.Item value="chart">Chart</SegmentedControl.Item>
          <SegmentedControl.Item value="table">Table</SegmentedControl.Item>
        </SegmentedControl.Root>
      </Flex>

      <Card mb="4">
        <div className="attendance-graph__controls">
          <Text size="2" weight="medium" color="gray">
            Time range
          </Text>
          <Box>
            <SegmentedControl.Root value={range} onValueChange={(v) => setRange(v as RangePreset)} size="1">
              {RANGE_OPTIONS.map((opt) => (
                <SegmentedControl.Item key={opt.value} value={opt.value}>
                  {opt.label}
                </SegmentedControl.Item>
              ))}
            </SegmentedControl.Root>
          </Box>

          {venues.length > 0 && (
            <>
              <Text size="2" weight="medium" color="gray">
                Venues
              </Text>
              <Flex gap="2" wrap="wrap" align="center">
                {venues.map((v) => {
                  const on = activeVenues.has(v.id);
                  return (
                    <button
                      key={v.id}
                      type="button"
                      className={on ? 'attendance-graph__chip attendance-graph__chip--on' : 'attendance-graph__chip'}
                      aria-pressed={on}
                      onClick={() => setSelectedVenues((prev) => toggleInSet(prev ?? new Set(venues.map((x) => x.id)), v.id))}
                    >
                      <span className="attendance-graph__swatch" style={{ background: colorForVenue(v.id, venues) }} />
                      {v.name}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="attendance-graph__link"
                  onClick={() =>
                    setSelectedVenues(
                      activeVenues.size === venues.length ? new Set() : new Set(venues.map((v) => v.id))
                    )
                  }
                >
                  {activeVenues.size === venues.length ? 'Clear all' : 'Select all'}
                </button>
              </Flex>
            </>
          )}

          {view === 'chart' && (
            <>
              <Text size="2" weight="medium" color="gray">
                Metric
              </Text>
              <Box>
                <SegmentedControl.Root value={metric} onValueChange={(v) => setMetric(v as Metric)} size="1">
                  {METRIC_OPTIONS.map((opt) => (
                    <SegmentedControl.Item key={opt.value} value={opt.value}>
                      {opt.label}
                    </SegmentedControl.Item>
                  ))}
                </SegmentedControl.Root>
              </Box>

              <Text size="2" weight="medium" color="gray">
                Lines
              </Text>
              <Flex gap="2" wrap="wrap">
                {OVERLAY_OPTIONS.map((opt) => {
                  const on = overlays.has(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={on ? 'attendance-graph__chip attendance-graph__chip--on' : 'attendance-graph__chip'}
                      aria-pressed={on}
                      onClick={() => setOverlays((prev) => toggleInSet(prev, opt.value))}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </Flex>
            </>
          )}
        </div>
      </Card>

      {loading && (
        <Text size="2" color="gray">
          Loading attendance…
        </Text>
      )}

      {error && !loading && (
        <Text size="2" color="red">
          {error}
        </Text>
      )}

      {!loading && !error && filtered.length === 0 && (
        <Text size="2" color="gray">
          No played nights for this selection yet.
        </Text>
      )}

      {!loading && !error && filtered.length > 0 && (
        <>
          <Grid columns={{ initial: '1', sm: '2', md: '3' }} gap="3" mb="4">
            {summaries.map((s) => (
              <Card key={s.venue.id}>
                <Flex align="center" gap="2" mb="3">
                  <span className="attendance-graph__swatch" style={{ background: s.color }} />
                  <Text size="2" weight="bold" truncate>
                    {s.venue.name}
                  </Text>
                </Flex>
                <Grid columns="3" gap="2">
                  <Stat label="Teams / night" value={s.teamsPerNight.toFixed(1)} />
                  <Stat label="People / night" value={s.peoplePerNight.toFixed(1)} />
                  <Stat label="Per team" value={s.peoplePerTeam.toFixed(1)} />
                </Grid>
                <Text as="div" size="1" color="gray" mt="2">
                  {s.nights} nights
                  {s.recentTeamsDelta !== null && (
                    <>
                      {' · last '}
                      {ROLLING_WINDOW}
                      {' vs prior '}
                      {ROLLING_WINDOW}
                      {': '}
                      <Text color={s.recentTeamsDelta >= 0.05 ? 'green' : s.recentTeamsDelta <= -0.05 ? 'red' : 'gray'}>
                        {s.recentTeamsDelta >= 0 ? '+' : '−'}
                        {Math.abs(s.recentTeamsDelta).toFixed(1)} teams
                      </Text>
                    </>
                  )}
                </Text>
              </Card>
            ))}
          </Grid>

          {view === 'chart' ? (
            <AttendanceChart points={filtered} venues={venues} kinds={kinds} overlays={overlays} />
          ) : (
            <AttendanceTable points={filtered} venues={venues} />
          )}
        </>
      )}
    </Box>
  );
}
